import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'utils/notify'

const initialState = {
  sponsorshipLevels: [],
  sponsorshipLevel: null,
  inProgress: false,
  error: null,
  currentPage: 0,
  pageSize: 10
}

const sponsorshipLevelSlice = createSlice({
  name: 'sponsorshipLevel',
  initialState,
  reducers: {
    setSponsorshipLevels: (state, action) => {
      state.sponsorshipLevels = action.payload
    },
    setSponsorshipLevel: (state, action) => {
      state.sponsorshipLevel = action.payload
    },
    setInProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.sponsorshipLevels = []
      state.sponsorshipLevel = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const getSponsorshipLevels = (conferenceId) => async (dispatch) => {
  try {
    dispatch(sponsorshipLevelSlice.actions.setInProgress(true))
    await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .orderBy('sponsorshipTier', 'desc')
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const sponsorshipLevels = []
        documents.forEach((doc) => {
          sponsorshipLevels.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(sponsorshipLevelSlice.actions.setSponsorshipLevels(sponsorshipLevels))
        dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
      })
  } catch (err) {
    console.error(err)
    toast.error('Could not load sponsorship levels.')
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  }
}

export const getSponsorshipLevelById = (id) => async (dispatch) => {
  try {
    dispatch(sponsorshipLevelSlice.actions.setInProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).doc(id).get()
    const e = snapshot.data()
    dispatch(
      sponsorshipLevelSlice.actions.setSponsorshipLevel({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  } catch (err) {
    dispatch(sponsorshipLevelSlice.actions.setError(err))
    dispatch(sponsorshipLevelSlice.actions.setInProgress(false))
  }
}

export const addSponsorshipLevel = async (payload, conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const sponsorshipLevels = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))
    const sponsorshipLevelCategories = sponsorshipLevels?.map(({ tierName }) => tierName)
    const categoryLevels = sponsorshipLevels?.map(({ sponsorshipTier }) => sponsorshipTier)

    if (sponsorshipLevelCategories.includes(payload.tierName)) {
      toast.warning('This tier name is already in use.')
      return
    }
    if (categoryLevels.includes(payload.sponsorshipTier)) {
      toast.warning('This sponsorship tier already exists.')
      return
    }

    const doc = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS).add({
      ...payload,
      conferenceId
    })
    return doc.id
  } catch (err) {
    console.error(err)
    toast.error('Could not add sponsorship level.')
  }
}

// export const updateSponsorshipLevel = async (payload, sponsorshipLevelId, conferenceId) => {
//   try {
//     const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
//       .where('conferenceId', '==', conferenceId)
//       .get()
//
//     const levels = snapshot.docs.map((doc) => ({
//       id: doc.id,
//       ...doc.data()
//     }))
//     const sponsorshipLevelCategories = levels
//       ?.filter(({ id }) => id !== sponsorshipLevelId)
//       ?.map(({ tierName }) => tierName)
//     const categoryLevels = levels
//       ?.filter(({ id }) => id !== sponsorshipLevelId)
//       ?.map(({ sponsorshipTier }) => sponsorshipTier)
//
//     if (sponsorshipLevelCategories.includes(payload.tierName)) {
//       toast.warning('This tier name is already in use.')
//       return
//     }
//     if (categoryLevels.includes(payload.sponsorshipTier)) {
//       toast.warning('This sponsorship tier already exists.')
//       return
//     }
//
//     await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
//       .doc(sponsorshipLevelId)
//       .update(payload)
//     return sponsorshipLevelId
//   } catch (err) {
//     console.error(err)
//     toast.error('Failed to update sponsorship level')
//   }
// }

export const updateSponsorshipLevel = async (payload, sponsorshipLevelId, conferenceId) => {
  try {
    // Fetch current sponsorship level to compare permissions
    const levelDoc = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .doc(sponsorshipLevelId)
      .get()

    const currentLevel = levelDoc.exists ? levelDoc.data() : null
    if (!currentLevel) {
      toast.error('Sponsorship level not found')
      return
    }

    // Check for permission removals
    const permissionsRemoved = {
      logo: currentLevel.allowLogo && !payload.allowLogo,
      adBanner: currentLevel.allowAdBanner && !payload.allowAdBanner,
      brochure: currentLevel.allowBrochure && !payload.allowBrochure
    }

    // Validate the level name and tier aren't duplicates
    const snapshot = await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const levels = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))

    const sponsorshipLevelCategories = levels
      ?.filter(({ id }) => id !== sponsorshipLevelId)
      ?.map(({ tierName }) => tierName)

    const categoryLevels = levels
      ?.filter(({ id }) => id !== sponsorshipLevelId)
      ?.map(({ sponsorshipTier }) => sponsorshipTier)

    if (sponsorshipLevelCategories.includes(payload.tierName)) {
      toast.warning('This tier name is already in use.')
      return
    }

    if (categoryLevels.includes(payload.sponsorshipTier)) {
      toast.warning('This sponsorship tier already exists.')
      return
    }

    // If any permissions were removed, handle affected organizations
    const hasRemovedPermissions = Object.values(permissionsRemoved).some(Boolean)

    if (hasRemovedPermissions) {
      // Find all organizations using this level
      const orgsSnapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
        .where('sponsorshipLevelId', '==', sponsorshipLevelId)
        .get()

      const updatePromises = []
      const storageDeletePromises = []
      const deletedImageCount = { logo: 0, adBanner: 0, brochure: 0 }

      // Process each organization to remove disallowed images
      orgsSnapshot.docs.forEach((doc) => {
        const org = doc.data()
        const orgId = doc.id
        const updateData = {}

        // Check and mark fields to be cleared based on removed permissions
        if (permissionsRemoved.logo && org.logoImageName) {
          // Add storage deletion promise
          storageDeletePromises.push(
            storageRef
              .child(`${conferenceId}/${Collection.ORGANIZATIONS}/${org.logoImageName}`)
              .delete()
              .catch((err) => console.error(`Error deleting logo for org ${orgId}:`, err))
          )

          updateData.logoImageName = null
          deletedImageCount.logo++
        }

        if (permissionsRemoved.adBanner && org.adImageName) {
          // Add storage deletion promise
          storageDeletePromises.push(
            storageRef
              .child(`${conferenceId}/${Collection.ORGANIZATIONS}/${org.adImageName}`)
              .delete()
              .catch((err) => console.error(`Error deleting ad banner for org ${orgId}:`, err))
          )

          updateData.adImageName = null
          updateData.bannerClickUrl = null
          deletedImageCount.adBanner++
        }

        if (permissionsRemoved.brochure && org.brochureImageName) {
          // Add storage deletion promise
          storageDeletePromises.push(
            storageRef
              .child(`${conferenceId}/${Collection.ORGANIZATIONS}/${org.brochureImageName}`)
              .delete()
              .catch((err) => console.error(`Error deleting brochure for org ${orgId}:`, err))
          )

          updateData.brochureImageName = null
          deletedImageCount.brochure++
        }

        // Only update if we have fields to update
        if (Object.keys(updateData).length > 0) {
          updatePromises.push(
            FirestoreDatabase.collection(Collection.ORGANIZATIONS).doc(orgId).update(updateData)
          )
        }
      })

      // Execute all storage deletion operations (even if some fail)
      if (storageDeletePromises.length > 0) {
        await Promise.allSettled(storageDeletePromises)
      }

      // Execute all database updates
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises)

        // Prepare notification message about removed assets
        const deletedTypes = []
        if (deletedImageCount.logo > 0) deletedTypes.push(`${deletedImageCount.logo} logos`)
        if (deletedImageCount.adBanner > 0)
          deletedTypes.push(`${deletedImageCount.adBanner} ad banners`)
        if (deletedImageCount.brochure > 0)
          deletedTypes.push(`${deletedImageCount.brochure} brochures`)

        if (deletedTypes.length > 0) {
          toast.info(
            `Removed ${deletedTypes.join(', ')} from organizations due to permission changes`
          )
        }
      }
    }

    // Finally update the sponsorship level
    await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .doc(sponsorshipLevelId)
      .update(payload)

    return sponsorshipLevelId
  } catch (err) {
    console.error(err)
    toast.error('Failed to update sponsorship level')
  }
}

export const deleteSponsorshipLevel = async (sponsorshipLevelId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.ORGANIZATIONS)
      .where('sponsorshipLevelId', '==', sponsorshipLevelId)
      .get()
    if (!snapshot.empty) {
      toast.error('Cannot delete — this sponsorship level is currently in use.')
      return
    }
    await FirestoreDatabase.collection(Collection.SPONSORSHIP_LEVELS)
      .doc(sponsorshipLevelId)
      .delete()
    toast.success('Sponsorship level deleted.')
  } catch (err) {
    toast.error('Could not delete sponsorship level.')
  }
}

export const resetSponsorshipLevel = () => (dispatch) =>
  dispatch(sponsorshipLevelSlice.actions.setSponsorshipLevel(null))

export default sponsorshipLevelSlice
